import {
    CandidateInterviewResult,
    convertDateToBE,
    createError,
    RelodeApiError,
    SetInterviewSubmitStatus,
} from "@relode/core";
import { CandidateInterviewSendNotificationRequest } from "../../../../shared/backend/controllers/candidates";

import { CombinedCandidatesFeatureActionTypes } from "..";
import { converters } from "../../../../shared/backend";
import { candidates } from "../../../../shared/backend/controllers";
import { SetHiredCandidateStatus } from "../../../../shared/components/candidate";
import { HiredCandidate } from "../../../../shared/types";
import { CandidateState } from "../candidateState";
import { creators } from "./statusChangeActionTypes";

export const useStatusChangeActions = (
    state: CandidateState,
    dispatch: React.Dispatch<CombinedCandidatesFeatureActionTypes>
) => {
    const reject = async (
        leadId: number,
        jobId: number,
        reasonId: number,
        reason: string,
        hiredEndDate?: Date,
        candidateConcurrencyToken?: string
    ) => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            await candidates.candidateService.reject(
                jobId,
                leadId,
                reasonId,
                reason,
                convertDateToBE(hiredEndDate),
                candidateConcurrencyToken
            );

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "moved to rejected"
                )
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.updateCandidateJobStatusFailure(err));
                    return;
                }
            }
            throw createError(err, "client.statusChangeActions.reject");
        }
    };

    const interview = async (
        leadId: number,
        jobId: number,
        data: CandidateInterviewResult,
        candidateConcurrencyToken: string,
        setInterviewSubmitStatus: SetInterviewSubmitStatus
    ): Promise<void> => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = converters.convertCandidateInterviewRequest(
                data,
                candidateConcurrencyToken
            );
            await candidates.candidateService.interview(leadId, jobId, request);

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "moved to interviewing"
                )
            );

            setInterviewSubmitStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.updateCandidateJobStatusFailure(err));
                    setInterviewSubmitStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "client.statusChangeActions.interview");
        }
    };

    const editInterview = async (
        leadId: number,
        jobId: number,
        data: CandidateInterviewResult,
        candidateConcurrencyToken: string,
        setInterviewSubmitStatus: SetInterviewSubmitStatus
    ): Promise<void> => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = converters.convertCandidateInterviewRequest(
                data,
                candidateConcurrencyToken
            );
            await candidates.candidateService.editInterviewDetails(
                leadId,
                jobId,
                request
            );

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "interview updated"
                )
            );

            setInterviewSubmitStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.interviewAddEditFailure());
                    setInterviewSubmitStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "client.statusChangeActions.editInterview");
        }
    };

    const sendInterviewNotification = async (
        leadId: number,
        jobId: number,
        interviewId: number,
        candidateConcurrencyToken: string,
        subject: string,
        recipientIds: number[]
    ): Promise<boolean> => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = new CandidateInterviewSendNotificationRequest(
                candidateConcurrencyToken,
                subject,
                recipientIds
            );

            await candidates.candidateService.sendInterviewNotification(
                leadId,
                jobId,
                interviewId,
                request
            );

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "interview reminder sent"
                )
            );
            return true;
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return false;
                if (err.isClientError()) {
                    dispatch(creators.interviewAddEditFailure());
                    return false;
                }
            }
            throw createError(
                err,
                "client.statusChangeActions.sendInterviewNotification"
            );
        }
    };

    const addInterview = async (
        leadId: number,
        jobId: number,
        data: CandidateInterviewResult,
        candidateConcurrencyToken: string,
        setInterviewSubmitStatus: SetInterviewSubmitStatus
    ): Promise<void> => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = converters.convertCandidateInterviewRequest(
                data,
                candidateConcurrencyToken
            );
            await candidates.candidateService.addNewInterview(
                leadId,
                jobId,
                request
            );

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "interview added"
                )
            );

            setInterviewSubmitStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.interviewAddEditFailure());
                    setInterviewSubmitStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "client.statusChangeActions.addInterview");
        }
    };

    const offer = async (
        leadId: number,
        jobId: number,
        candidateConcurrencyToken?: string
    ) => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = converters.convertCandidateOfferRequest(
                candidateConcurrencyToken
            );
            await candidates.candidateService.offer(leadId, jobId, request);

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "moved to offered"
                )
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.updateCandidateJobStatusFailure(err));
                    return;
                }
            }
            throw createError(err, "client.statusChangeActions.offer");
        }
    };

    const hire = async (
        jobId: number,
        leadId: number,
        hiredCandidate: HiredCandidate,
        setHiredCandidateStatus: SetHiredCandidateStatus
    ) => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = converters.convertHireRequest(hiredCandidate);
            await candidates.candidateService.hire(jobId, leadId, request);

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "moved to hired"
                )
            );

            setHiredCandidateStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.hireCandidateFailure(err));
                    setHiredCandidateStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "client.statusChangeActions.hire");
        }
    };

    const editHireDetails = async (
        jobId: number,
        leadId: number,
        hiredCandidate: HiredCandidate,
        setHiredCandidateStatus: SetHiredCandidateStatus
    ) => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = converters.convertHireRequest(hiredCandidate);

            await candidates.candidateService.editHireDetails(
                jobId,
                leadId,
                request
            );

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "hired info updated"
                )
            );
            setHiredCandidateStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.hireEditFailure(err));
                    setHiredCandidateStatus("failure", err);
                    return;
                }
            }
            throw createError(
                err,
                "client.statusChangeActions.editHireDetails"
            );
        }
    };

    const updateHiredEndDate = async (
        jobId: number,
        leadId: number,
        hiredCandidate: HiredCandidate
    ) => {
        dispatch(creators.updateCandidateJobStatus());
        try {
            const request = converters.convertHireRequest(hiredCandidate);

            await candidates.candidateService.editHireDetails(
                jobId,
                leadId,
                request
            );

            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );

            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );

            dispatch(
                creators.updateCandidateJobStatusSuccess(
                    details,
                    "hired info updated"
                )
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.hireEditFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.statusChangeActions.updateHiredEndDate"
            );
        }
    };

    return {
        reject,
        interview,
        editInterview,
        addInterview,
        offer,
        hire,
        editHireDetails,
        sendInterviewNotification,
        updateHiredEndDate,
    };
};

export type StatusChangeActions = ReturnType<typeof useStatusChangeActions>;
